const libFableServiceProviderBase = require('fable-serviceproviderbase');

/** @typedef {import('fable')} Fable */

/**
 * Headlight Hint Highlighting service.
 */
class HeadlightHintHighlighting extends libFableServiceProviderBase
{
	/**
	 * @param {Fable} pFable - The fable instance
	 * @param {any} pOptions - Options for this service
	 * @param {any} pServiceHash - The fable service hash for this instance
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {import('fable') & { PictApplication?: any, AppData?: any, MetaTemplate?: any }} */
		this.pict = this.fable; // for parity with pict providers / views

		this.serviceType = 'HeadLightHintHighlighting';

		//FIXME: we should use the manifest tech for this, I think
		const pathEval = (path, record) => path
			.split(/[.[\]]/g)
			.reduce((p, c) => p && p[c],
			{
				Record: record,
				AppData: this.pict.AppData,
			});

		// create a label for the hint forumla
		this.pict.MetaTemplate.addPattern('{~HintLabel:', '~}', (pHash, pData, pContext) =>
		{
			const record = pathEval(pHash, pData);
			// NOTE: this is to support the old way until we migrate everything to the new way
			const hint = record?.PictForm?.Hint || record?.PictForm?.Formula || record?.hint;
			let hintConnections = record?.hintConnections;
			// parse the formula for the hint connections (non-numeric values are hint connections)
			if (!hintConnections && record?.PictForm?.Formula)
			{
				const formula = record.PictForm.Formula;
				const formulaArray = formula.split(/[\s\+\-\*\/\(\)]/);
				hintConnections = formulaArray.filter((value) => isNaN(value) && value !== '');
			}
			let template = ``;
			if (hint)
			{
				template =
				`<span class="hint-label is-pulled-right" data-hint-connections="${hintConnections}" onclick="this.pict.services.HeadlightHintHighlighting.toggleHintConnections(event)">
					<span class="icon"><i class="fas"></i></span>
					<span>${hint}</span>
				</span>`;
			}
			return template;
		});

		// create an ordinal tag for hint indentification
		this.pict.MetaTemplate.addPattern('{~HintOrdinal:', '~}', (pHash, pData, pContext) =>
		{
			const record = pathEval(pHash, pData);
			// NOTE: this is to support the old way until we migrate everything to the new way
			const ordinal = record?.PictForm?.Ordinal || record?.ordinal;
			return ordinal ? `<span class="hint-ordinal tag">${ordinal}</span>` : '';
		});

		// create a connection tag for hint connections
		this.pict.MetaTemplate.addPattern('{~HintConnection:', '~}', (pHash, pData, pContext) =>
		{
			const data = pathEval(pHash, pData);
			let template = ``;
			if (data)
			{
				template = `data-hint-connection="${data}"`;
			}
			return template;
		});
	}

	/**
	 * Find the parent element of an element that matches a selector
	 * @param {EventTarget} target - the element to start from
	 * @param {string} selector - the selector to match
	 * @return {Element[]} - an array of parent elements that match the selector
	 * @example
	 * const parents = this.findParents(el, '.parent-class');
	 * // parents = [HTMLElement, HTMLElement, HTMLElement]
	**/
	parents(target, selector)
	{
		if (!(target instanceof Element))
		{
			return [];
		}
		/** @type {Element} */
		let el = target;
		const parents = [];
		while (el.parentNode instanceof Element && (el = el.parentNode))
		{
			if (!selector || el.matches(selector))
			{
				parents.push(el);
			}
		}
		return parents;
	}

	/**
	 * This handles the highlighting of hint connections on the UI.
	 * If a hint connection is clicked, it will highlight the connection and the origin row to show the relationship
	 * @param {Event} event
	 * @memberof Application
	 *
	**/
	toggleHintConnections(event)
	{
		// remove rows that are currently active
		document.querySelectorAll('.hint-highlight-connection').forEach((hint) =>
		{
			hint.classList.remove('hint-highlight-connection');
		});
		// toggle active class on the hint connection
		if (this.parents(event.currentTarget, '.hint-highlight-origin').length)
		{
			this.parents(event.currentTarget, '.hint-highlight-origin')[0].classList.remove('hint-highlight-origin');
			return;
		}
		// turn off all other hint connections labels
		document.querySelectorAll('.hint-highlight-origin').forEach((hint) =>
		{
			hint.classList.remove('hint-highlight-origin');
		});
		// turn on the active class for this hint connection
		if (this.parents(event.currentTarget, '[data-hint-connection]').length)
		{
			this.parents(event.currentTarget, '[data-hint-connection]')[0].classList.add('hint-highlight-origin');
		}
		// for each row that has a hint connection, add the active class
		const connectionsList = event.currentTarget instanceof Element && event.currentTarget.getAttribute('data-hint-connections').split(',');
		if (connectionsList && connectionsList.length)
		{
			connectionsList.forEach((connection) =>
			{
				// NOTE: need to handle non-unqiue connections/ordinals (this only hightlights the first one found)
				document.querySelector(`[data-hint-connection="${connection}"]`)?.classList?.add('hint-highlight-connection');
			});
		}
	}
}

module.exports = HeadlightHintHighlighting;
